import { useQuery } from '@tanstack/react-query';
import { isCancel } from 'axios';
import { StatusCodes } from 'http-status-codes';

import { DEFAULT_RETRY_DELAY, refetchUnlessServerError } from '@/config/query-client';
import { httpStatusOf } from '@/utils/httpStatusOf';

import useStores from '../../../hooks/useStores';
import type { ISinglePatientRow } from './types';

const isPatientNotFoundError = (error: unknown): boolean =>
  httpStatusOf(error) === StatusCodes.NOT_FOUND;

type PatientQuery = { state: { error: unknown; data: unknown } };

const isRefreshableOnReturn = ({ state }: PatientQuery): boolean =>
  !state.error || (state.data !== undefined && !isPatientNotFoundError(state.error));

const isRefreshableUnlessServerError = (query: PatientQuery): boolean =>
  isRefreshableOnReturn(query) && refetchUnlessServerError(query);

const isNetworkFailureWithoutData = ({ state }: PatientQuery): boolean =>
  !!state.error &&
  state.data === undefined &&
  httpStatusOf(state.error) === undefined &&
  !isCancel(state.error);

const isRecoverableOnFocus = isRefreshableUnlessServerError;

const isRecoverableOnReconnect = (query: PatientQuery): boolean =>
  isRefreshableUnlessServerError(query) || isNetworkFailureWithoutData(query);

//TODO: Move to src/hooks instead of a specific page
export default function useSinglePatientService(patientUuid: string) {
  const { singlePatientStore } = useStores();

  const result = useQuery<ISinglePatientRow>({
    queryKey: ['camel', 'patients', patientUuid],
    retryDelay: DEFAULT_RETRY_DELAY,
    enabled: !!patientUuid,
    refetchOnWindowFocus: isRecoverableOnFocus,
    refetchOnReconnect: isRecoverableOnReconnect,
  });

  const isPatientNotFound = isPatientNotFoundError(result.error);

  return {
    patient: result?.data,
    state: singlePatientStore,
    isLoading: !!patientUuid && result.isLoading,
    error: result.error,
    isPatientNotFound,
    isPatientLoadFailed: !!result.error && !result.data && !isPatientNotFound,
    refetch: result.refetch,
  };
}
